import React, { useCallback, useState, useEffect, useRef } from 'react';
import { DataGrid, Column, FilterRow, Scrolling, SearchPanel, Editing } from 'devextreme-react/data-grid';
import { Button } from 'devextreme-react';

import ProcessingResult from '../../framework/ProcessingResult';

import CustomStore from 'devextreme/data/custom_store';
import GlobalLoader from '../loader/GlobalLoader';

import PathCredentialCreateForm, { PathCredentialCreateFormHandle } from '../path-credential-create/PathCredentialCreateForm';
import PathCredentialShowForm, { PathCredentialShowFormHandle } from '../path-credential-show/PathCredentialShowForm';

import CustomerApiCredentialApi from '../../api/CustomerApiCredentialApi';

import CustomerApiCredentialListDto from '../../classes/dtos/credentialmodule/CustomerApiCredentialListDto';


const PathCredentialGrid: React.FC = () => {

    const pathCredentialCreateFormHandleRef = useRef<PathCredentialCreateFormHandle>(null);
    const pathCredentialShowFormHandleRef = useRef<PathCredentialShowFormHandle>(null);

    const [pathCredentials, setPathCredentials] = useState<Array<CustomerApiCredentialListDto> | null>(null);

    function created() {
        setPathCredentials(null);
    }

    const refresh = useCallback(() => {
        if (!pathCredentials) {
            CustomerApiCredentialApi.GetCustomerApiCredentialsAsync().then((allPathCredentials: ProcessingResult<CustomerApiCredentialListDto[]>) => {
                if (allPathCredentials.IsFailed()) {
                    // TODO: Handle failure case (e.g., logout)
                } else {
                    setPathCredentials(allPathCredentials.Result);
                }
            });
        }
    }, [pathCredentials]);

    // UseEffect to refresh when component mounts
    useEffect(() => {
        refresh();
    }, [refresh]);

    function createPathCredentials(): void {
        if (pathCredentialCreateFormHandleRef.current !== null) {
            pathCredentialCreateFormHandleRef.current.Open();
        }
    };

    const customDataSource = new CustomStore({
        key: 'Id',
        load: () => Promise.resolve(pathCredentials ?? []),
        update: (key: number, values: CustomerApiCredentialListDto): any => {
            const oldData = pathCredentials?.find((item) => item.Id === key);
            const updatedItem = { ...oldData, ...values };
            return CustomerApiCredentialApi.UpdateCustomerApiCredentialsAsync(key, updatedItem.ValidityStart, updatedItem.ValidityEnd, updatedItem.Name)
                .then((e) => {
                    if (e.IsFailed()) {
                        throw new Error("Update failed");
                    } else {
                        setPathCredentials(prev =>
                            prev!.map(item => item.Id === key ? { ...item, ...values } : item)
                        );
                        return { ...values, Id: key };
                    }
                });
        },
        remove: (key: number): any => {
            return CustomerApiCredentialApi.DeleteCustomerApiCredentialsAsync(key)
                .then((e) => {
                    if (e.IsFailed()) {
                        throw new Error("Delete failed");
                    }
                    setPathCredentials(null);
                });
        }
    });

    const handleClick = (customerApiCredentialListDto: CustomerApiCredentialListDto) => {
        if (pathCredentialShowFormHandleRef.current !== null) {
            pathCredentialShowFormHandleRef.current.Open(customerApiCredentialListDto.Id);
        }
    };

    const renderLinkCell = (cellData: any) => {
        return (
            <a onClick={(e) => {
                e.preventDefault();
                handleClick(cellData.data);
            }}>
                Show secret
            </a>
        );
    };

    if (!pathCredentials) {
        return <GlobalLoader />
    }

    return (
        <div className={"block-card"}>
            <div className={"block-card__header"}>
                <h2>{"All your credentials"}</h2>
                <Button icon="plus" width={140} text="Create" type="default" stylingMode="contained" onClick={createPathCredentials} disabled={false} />
            </div>
            <DataGrid
                id="dataGrid"
                dataSource={customDataSource}
                allowColumnReordering={true}
                allowColumnResizing={true}
                columnAutoWidth={false}
                hoverStateEnabled={true}
                repaintChangesOnly={true}
                height='calc(100vh - 400px)'
                onRowUpdated={(e) => {
                    e.component.refresh();
                }}>
                <Scrolling mode="infinite" />
                <FilterRow visible={false} />
                <SearchPanel visible={false} />
                <Editing
                    mode="row"
                    allowUpdating={true}
                    allowDeleting={true}
                    allowAdding={false} />
                <Column caption="NAME" dataField="Name" dataType="string" allowSorting={true} />
                <Column caption="CREATED BY" dataField="CreatedBy.Label" dataType="string" allowSorting={true} allowEditing={false} />
                <Column caption="VALIDITY START" dataField="ValidityStart" dataType="date" allowSorting={true} />
                <Column caption="VALIDITY END" dataField="ValidityEnd" dataType="date" allowSorting={true} />
                <Column caption="TYPE" dataField="TypeLabel" dataType="string" allowSorting={true} allowEditing={false} />
                <Column caption="SECRET" allowSorting={false} allowEditing={false} cellRender={renderLinkCell} />
            </DataGrid>
            <PathCredentialCreateForm ref={pathCredentialCreateFormHandleRef} Created={created} />
            <PathCredentialShowForm ref={pathCredentialShowFormHandleRef} />
        </div>
    );
};

export default PathCredentialGrid;
